import React, { useEffect, useState } from "react";
import { treePlanting } from "../controller";
import Tree1 from "../assets/images/tree_mini_game_2026/Tree-1.png";
import Tree2 from "../assets/images/tree_mini_game_2026/Tree-2.png";
import Tree3 from "../assets/images/tree_mini_game_2026/Tree-3.png";
import Tree4 from "../assets/images/tree_mini_game_2026/Tree-4.png";
import { FaGift, FaTint, FaCheckCircle, FaTrophy, FaMedal, FaStar, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Noti from "./Noti";

const TreePlanting = ({ userEventId, event = "minigametet2026" }) => {
  const [treeInfo, setTreeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canClaim, setCanClaim] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isWatering, setIsWatering] = useState(false);
  const [rewardModal, setRewardModal] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsPagination, setRewardsPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (userEventId) {
      fetchTreeInfo();
      checkDailyTurnStatus();
      fetchUserRewards(1);
    }
  }, [userEventId, event]);

  const fetchTreeInfo = async () => {
    try {
      setLoading(true);
      const response = await treePlanting.getUserTreeInfo(userEventId, event);
      if (response?.success) {
        setTreeInfo(response.data);
      }
    } catch (error) {
      console.error("Error fetching tree info:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkDailyTurnStatus = async () => {
    try {
      const response = await treePlanting.checkDailyTurn(userEventId, event);
      if (response?.success) {
        setCanClaim(response.data.canClaim);
      }
    } catch (error) {
      console.error("Error checking daily turn:", error);
    }
  };

  const handleClaimDailyTurn = async () => {
    try {
      const response = await treePlanting.claimDailyTurn({
        userEventId,
        event,
      });
      if (response?.success) {
        setNotification({
          type: "success",
          message: response.message,
        });
        setCanClaim(false);
        fetchTreeInfo();
      } else {
        setNotification({
          type: "info",
          message: response.message,
        });
      }
    } catch (error) {
      console.error("Error claiming daily turn:", error);
      setNotification({
        type: "error",
        message: "Có lỗi xảy ra khi nhận lượt!",
      });
    }
  };

  const fetchUserRewards = async (page = 1) => {
    try {
      setRewardsLoading(true);
      const response = await treePlanting.getUserRewards(userEventId, event, page, 10);
      if (response?.success) {
        setRewards(response.data.rewards);
        setRewardsPagination(response.data.pagination);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Error fetching rewards:", error);
    } finally {
      setRewardsLoading(false);
    }
  };

  const handleWaterTree = async () => {
    if (isWatering || treeTurns === 0) return;
    
    try {
      setIsWatering(true);
      const response = await treePlanting.waterTree({
        userEventId,
        event,
      });
      
      if (response?.success) {
        setNotification({
          type: "success",
          message: response.message,
        });
        
        if (response.reward) {
          setTimeout(() => {
            setRewardModal(response.reward);
          }, 1200);
        }
        
        if (response.levelChanged) {
          setTimeout(() => {
            setNotification({
              type: "success",
              message: `🎉 Chúc mừng! Cây của bạn đã lên level ${response.treeLevel}! 🌳`,
            });
          }, 1500);
        }
        
        fetchTreeInfo();
        fetchUserRewards(currentPage);
      } else {
        setNotification({
          type: "warning",
          message: response.message || "Bạn không còn lượt tưới cây!",
        });
      }
    } catch (error) {
      console.error("Error watering tree:", error);
      setNotification({
        type: "error",
        message: "Có lỗi xảy ra khi tưới cây!",
      });
    } finally {
      // Đảm bảo nút được enable lại sau khi API hoàn thành
      setTimeout(() => {
        setIsWatering(false);
      }, 800);
    }
  };

  const getRewardContent = (reward) => {
    if (!reward) return null;
    
    switch (reward.type) {
      case "GOLD":
        return {
          icon: <FaTrophy className="text-6xl text-yellow-400" />,
          title: "🎉 CHÚC MỪNG! 🎉",
          message: "Bạn đã nhận được H vàng!",
          bgGradient: "from-yellow-400 via-yellow-500 to-amber-600",
          confettiColor: "yellow"
        };
      case "SILVER":
        return {
          icon: <FaMedal className="text-6xl text-gray-300" />,
          title: "🎊 TUYỆT VỜI! 🎊",
          message: "Bạn đã nhận được H bạc!",
          bgGradient: "from-gray-300 via-gray-400 to-gray-500",
          confettiColor: "silver"
        };
      case "BRONZE":
        return {
          icon: <FaMedal className="text-6xl text-orange-700" />,
          title: "👏 XUẤT SẮC! 👏",
          message: "Bạn đã nhận được H đồng!",
          bgGradient: "from-orange-600 via-orange-700 to-orange-800",
          confettiColor: "bronze"
        };
      default:
        return {
          icon: <FaStar className="text-6xl text-pink-400" />,
          title: "✨ LỜI CHÚC TẾT ✨",
          message: reward.content || reward.message,
          bgGradient: "from-pink-400 via-purple-500 to-indigo-600",
          confettiColor: "purple"
        };
    }
  };

  const getTreeImage = (points) => {
    if (points >= 0 && points <= 10) return Tree1;
    if (points >= 11 && points <= 20) return Tree2;
    if (points >= 21 && points <= 30) return Tree3;
    return Tree4;
  };

  const getRewardIcon = (type) => {
    switch (type) {
      case "GOLD":
        return <FaTrophy className="text-yellow-400" />;
      case "SILVER":
        return <FaMedal className="text-gray-300" />;
      case "BRONZE":
        return <FaMedal className="text-orange-700" />;
      case "BLESSING":
        return <FaStar className="text-pink-400" />;
      default:
        return <FaGift className="text-blue-400" />;
    }
  };

  const getRewardBadgeClass = (type) => {
    switch (type) {
      case "GOLD":
        return "bg-gradient-to-r from-yellow-400 to-amber-500 text-white";
      case "SILVER":
        return "bg-gradient-to-r from-gray-300 to-gray-500 text-white";
      case "BRONZE":
        return "bg-gradient-to-r from-orange-600 to-orange-800 text-white";
      case "BLESSING":
        return "bg-gradient-to-r from-pink-400 to-purple-600 text-white";
      default:
        return "bg-blue-500 text-white";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateRewardStats = () => {
    if (!rewards || rewards.length === 0) {
      return { GOLD: 0, SILVER: 0, BRONZE: 0, BLESSING: 0, total: 0 };
    }
    
    const stats = {
      GOLD: rewards.filter(r => r.rewardType === "GOLD").length,
      SILVER: rewards.filter(r => r.rewardType === "SILVER").length,
      BRONZE: rewards.filter(r => r.rewardType === "BRONZE").length,
      BLESSING: rewards.filter(r => r.rewardType === "BLESSING").length,
    };
    
    stats.total = stats.GOLD + stats.SILVER + stats.BRONZE + stats.BLESSING;
    return stats;
  };

  if (loading) {
    return (
      <div className="bg-white/20 w-11/12 mx-auto mt-14 rounded-3xl px-6 py-10 shadow-lg">
        <div className="text-center text-white text-lg">Đang tải thông tin cây...</div>
      </div>
    );
  }

  if (!treeInfo) {
    return null;
  }

  const treePoints = treeInfo.tree?.points || 0;
  const treeTurns = treeInfo.turns?.total || 0;
  const treeLevel = treeInfo.tree?.level || "Cây Mầm";

  return (
    <>
      {notification && (
        <Noti
          message={<span>{notification.message}</span>}
          onClose={() => setNotification(null)}
        />
      )}

      {rewardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
          <div className="relative max-w-md w-11/12 mx-auto animate-scaleIn">
            <div className={`bg-gradient-to-br ${getRewardContent(rewardModal).bgGradient} rounded-3xl shadow-2xl p-8 relative overflow-hidden`}>
              <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
              
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute animate-confetti"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `-${Math.random() * 20}px`,
                      animationDelay: `${Math.random() * 2}s`,
                      animationDuration: `${2 + Math.random() * 2}s`
                    }}
                  >
                    ✨
                  </div>
                ))}
              </div>

              <div className="relative z-10 text-center">
                <div className="mb-6 animate-bounce">
                  {getRewardContent(rewardModal).icon}
                </div>
                
                <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">
                  {getRewardContent(rewardModal).title}
                </h2>
                
                <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 mb-6 border-2 border-white/30">
                  <p className="text-xl font-bold text-white leading-relaxed">
                    {getRewardContent(rewardModal).message}
                  </p>
                </div>

                <button
                  onClick={() => setRewardModal(null)}
                  className="bg-white text-gray-800 font-bold py-3 px-8 rounded-full hover:scale-110 transition-transform shadow-xl hover:shadow-2xl"
                >
                  Tuyệt vời! 🎉
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white/20 w-11/12 mx-auto mt-14 rounded-3xl px-6 py-10 shadow-lg">
        <h2 className="text-3xl font-bold text-center text-[#ffc400] drop-shadow-sm mb-6">
          🌳 TRỒNG CÂY XANH
        </h2>

        <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
          <div className="flex-1 flex flex-col items-center">
            <div className={`relative ${isWatering ? 'animate-bounce' : ''}`}>
              <img
                src={getTreeImage(treePoints)}
                alt="Tree"
                className="w-64 h-64 object-contain drop-shadow-2xl"
              />
              {isWatering && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <FaTint className="text-blue-400 text-6xl animate-ping" />
                </div>
              )}
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-2xl font-bold text-white">{treeLevel}</p>
              <p className="text-lg text-white/80">{treePoints} điểm</p>
            </div>
          </div>

          <div className="flex-1 w-full max-w-md">
            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700 font-semibold">Lượt tưới cây:</span>
                  <span className="text-2xl font-bold text-blue-600">{treeTurns}</span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleClaimDailyTurn}
                  disabled={!canClaim}
                  className={`w-full py-3 px-6 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                    canClaim
                      ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:shadow-lg hover:scale-105 animate-pulse"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {canClaim ? (
                    <>
                      <FaGift className="text-xl" />
                      Nhận lượt miễn phí
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="text-xl" />
                      Đã nhận hôm nay
                    </>
                  )}
                </button>

                <button
                  onClick={handleWaterTree}
                  disabled={treeTurns === 0 || isWatering}
                  className={`w-full py-3 px-6 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                    treeTurns > 0 && !isWatering
                      ? "bg-gradient-to-r from-blue-400 to-blue-600 text-white hover:shadow-lg hover:scale-105"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
                  }`}
                >
                  <FaTint className={`text-xl ${isWatering ? 'animate-pulse' : ''}`} />
                  {isWatering ? "Đang tưới..." : "Tưới cây"}
                </button>
              </div>

              {treeTurns === 0 && (
                <div className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 text-sm">
                  Bạn đã hết lượt tưới cây! Hoàn thành nhiệm vụ để nhận thêm lượt.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rewards Section */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-center text-[#ffc400] drop-shadow-sm mb-6">
            🎁 PHẦN THƯỞNG ĐÃ NHẬN
          </h3>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-3xl font-bold text-center">{calculateRewardStats().total}</div>
              <div className="text-sm text-center mt-1 opacity-90">Tổng phần thưởng</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-center mb-1">
                <FaTrophy className="text-2xl" />
              </div>
              <div className="text-2xl font-bold text-center">{calculateRewardStats().GOLD}</div>
              <div className="text-xs text-center mt-1 opacity-90">H Vàng</div>
            </div>
            <div className="bg-gradient-to-br from-gray-300 to-gray-500 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-center mb-1">
                <FaMedal className="text-2xl" />
              </div>
              <div className="text-2xl font-bold text-center">{calculateRewardStats().SILVER}</div>
              <div className="text-xs text-center mt-1 opacity-90">H Bạc</div>
            </div>
            <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-center mb-1">
                <FaMedal className="text-2xl" />
              </div>
              <div className="text-2xl font-bold text-center">{calculateRewardStats().BRONZE}</div>
              <div className="text-xs text-center mt-1 opacity-90">H Đồng</div>
            </div>
            <div className="bg-gradient-to-br from-pink-400 to-purple-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-center mb-1">
                <FaStar className="text-2xl" />
              </div>
              <div className="text-2xl font-bold text-center">{calculateRewardStats().BLESSING}</div>
              <div className="text-xs text-center mt-1 opacity-90">Lời chúc</div>
            </div>
          </div>

          {/* Rewards Table */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {rewardsLoading ? (
              <div className="p-8 text-center text-gray-500">
                Đang tải danh sách phần thưởng...
              </div>
            ) : rewards && rewards.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Loại quà</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Nội dung</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Lượt tưới</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Ngày nhận</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rewards.map((reward, index) => (
                        <tr
                          key={reward.id}
                          className={`${
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          } hover:bg-blue-50 transition-colors`}
                        >
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getRewardBadgeClass(
                                reward.rewardType
                              )}`}
                            >
                              {getRewardIcon(reward.rewardType)}
                              {reward.rewardType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {reward.rewardContent}
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-semibold text-blue-600">
                            #{reward.wateringTurnNumber}
                          </td>
                          <td className="px-4 py-3 text-center text-xs text-gray-500">
                            {formatDate(reward.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {rewardsPagination && rewardsPagination.totalPages > 1 && (
                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Trang <span className="font-semibold">{rewardsPagination.page}</span> /{" "}
                        <span className="font-semibold">{rewardsPagination.totalPages}</span>
                        {" "}(Tổng {rewardsPagination.total} phần thưởng)
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchUserRewards(currentPage - 1)}
                          disabled={!rewardsPagination.hasPrevPage}
                          className={`px-3 py-1 rounded-lg flex items-center gap-1 text-sm font-semibold transition-all ${
                            rewardsPagination.hasPrevPage
                              ? "bg-blue-500 text-white hover:bg-blue-600"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          <FaChevronLeft className="text-xs" />
                          Trước
                        </button>
                        <button
                          onClick={() => fetchUserRewards(currentPage + 1)}
                          disabled={!rewardsPagination.hasNextPage}
                          className={`px-3 py-1 rounded-lg flex items-center gap-1 text-sm font-semibold transition-all ${
                            rewardsPagination.hasNextPage
                              ? "bg-blue-500 text-white hover:bg-blue-600"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          Sau
                          <FaChevronRight className="text-xs" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 text-center">
                <FaGift className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Chưa có phần thưởng nào</p>
                <p className="text-gray-400 text-sm mt-2">Hãy tưới cây để nhận phần quà nhé!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TreePlanting;
